<template>
  <div>
    <!-- Fil d'Ariane (Breadcrumb) accessible -->
    <b-card
      class="mb-3"
      aria-label="Fil d'Ariane de navigation"
    >
      <b-breadcrumb aria-label="Chemin de navigation">
        <b-breadcrumb-item to="/">
          <feather-icon
            icon="HomeIcon"
            size="16"
            class="mr-25"
            aria-hidden="true"
          />
          <span class="align-middle">Accueil</span>
        </b-breadcrumb-item>
        <b-breadcrumb-item to="/delivery-men">
          Livreurs
        </b-breadcrumb-item>
        <b-breadcrumb-item :to="{ name: 'delivery-man-show', params: { id: $route.params.id }}">
          Détail livreur
        </b-breadcrumb-item>
        <b-breadcrumb-item
          active
          aria-current="page"
        >
          Changer le mot de passe
        </b-breadcrumb-item>
      </b-breadcrumb>
    </b-card>

    <!-- État de chargement -->
    <b-overlay
      :show="isLoading"
      rounded="sm"
      spinner-variant="primary"
      aria-busy="isLoading"
      aria-live="polite"
    >
      <!-- État d'erreur -->
      <b-alert
        v-if="error"
        variant="danger"
        show
        aria-live="assertive"
        aria-label="Erreur lors du changement de mot de passe"
      >
        {{ error }}
      </b-alert>

      <!-- Formulaire de changement de mot de passe -->
      <b-card
        title="Changer le mot de passe"
        aria-label="Formulaire de changement de mot de passe du livreur"
      >
        <validation-observer
          ref="formValidator"
          v-slot="{ handleSubmit }"
        >
          <b-form
            aria-label="Formulaire de changement de mot de passe"
            @submit.prevent="handleSubmit(onSubmit)"
          >
            <b-card-text class="mb-3">
              <div
                class="alert alert-info"
                aria-live="polite"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Vous êtes en train de modifier le mot de passe pour le livreur : <strong>{{ deliveryManName }}</strong>. Veuillez choisir un mot de passe sécurisé (au moins 8 caractères, chiffres, majuscules et caractères spéciaux recommandés).</span>
              </div>
            </b-card-text>

            <!-- Champs de mot de passe -->
            <b-row>
              <!-- Champ : Nouveau mot de passe -->
              <b-col md="6">
                <b-form-group
                  label="Nouveau mot de passe *"
                  label-for="password"
                  description="Le mot de passe doit comporter au moins 8 caractères, inclure des chiffres, des majuscules et des caractères spéciaux."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Mot de passe"
                    rules="required|min:8"
                    vid="password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="password"
                        v-model="passwordForm.password"
                        :type="showPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : passwordStrength > 25"
                        placeholder="Entrez le nouveau mot de passe"
                        aria-label="Nouveau mot de passe du livreur"
                        aria-required="true"
                        aria-describedby="password-desc"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          aria-label="Afficher ou masquer le mot de passe"
                          @click="showPassword = !showPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="password-desc"
                      class="sr-only"
                    >Ce champ est requis pour garantir la sécurité des accès du livreur.</small>

                    <!-- Indicateur de force du mot de passe -->
                    <div class="mt-1">
                      <div class="d-flex justify-content-between mb-1">
                        <small>Force du mot de passe :</small>
                        <small class="text-right">{{ passwordStrengthText }}</small>
                      </div>
                      <b-progress
                        :value="passwordStrength"
                        :variant="passwordStrengthClass"
                        max="100"
                        height="8px"
                        aria-label="Indicateur de force du mot de passe"
                        aria-valuenow="passwordStrength"
                        aria-valuemax="100"
                        aria-valuemin="0"
                      />
                    </div>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Champ : Confirmation du mot de passe -->
              <b-col md="6">
                <b-form-group
                  label="Confirmer le mot de passe *"
                  label-for="confirmPassword"
                  description="Ressaisissez le mot de passe pour confirmation."
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Confirmation du mot de passe"
                    rules="required|confirmed:password"
                  >
                    <b-input-group class="input-group-merge">
                      <b-form-input
                        id="confirmPassword"
                        v-model="passwordForm.confirm_password"
                        :type="showConfirmPassword ? 'text' : 'password'"
                        :state="errors.length > 0 ? false : null"
                        placeholder="Confirmez le mot de passe"
                        aria-label="Confirmation du mot de passe du livreur"
                        aria-required="true"
                        aria-describedby="confirmPassword-desc"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          class="cursor-pointer"
                          aria-label="Afficher ou masquer la confirmation du mot de passe"
                          @click="showConfirmPassword = !showConfirmPassword"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <b-form-invalid-feedback aria-live="polite">
                      {{ errors[0] }}
                    </b-form-invalid-feedback>
                    <small
                      id="confirmPassword-desc"
                      class="sr-only"
                    >Ce champ est requis pour confirmer le nouveau mot de passe du livreur.</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>

            <div class="d-flex justify-content-between mt-4">
              <b-button
                variant="outline-secondary"
                :to="{ name: 'delivery-man-show', params: { id: $route.params.id }}"
                aria-label="Annuler et retourner au détail du livreur"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Annuler</span>
              </b-button>

              <b-button
                variant="primary"
                type="submit"
                :disabled="isSubmitting || !canSubmit"
                aria-label="Enregistrer le nouveau mot de passe du livreur"
              >
                <feather-icon
                  icon="SaveIcon"
                  class="mr-1"
                  aria-hidden="true"
                />
                <span>Enregistrer</span>
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import storeAuth from '@/store/store'
import {
  BCard,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BAlert,
  BOverlay,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BCardText,
  BRow,
  BCol,
  BProgress,
} from 'bootstrap-vue'

// Register the validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BBreadcrumb,
    BBreadcrumbItem,
    BButton,
    BAlert,
    BOverlay,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BCardText,
    BRow,
    BCol,
    BProgress,
  },

  data() {
    return {
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      deliveryMan: {},
      isLoading: false,
      isSubmitting: false,
      error: null,
      showPassword: false,
      showConfirmPassword: false,
      user: {},
    }
  },

  computed: {
    deliveryManName() {
      return this.deliveryMan.username || ''
    },

    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      // Length contribution
      if (password.length >= 8) strength += 25

      // Character type contribution
      if (/[A-Z]/.test(password)) strength += 25 // Uppercase
      if (/[0-9]/.test(password)) strength += 25 // Numbers
      if (/[^A-Za-z0-9]/.test(password)) strength += 25 // Special chars

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    canSubmit() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.user = storeAuth.state.user
    // Check if user is admin
    if (this.user.role !== 'admin') {
      this.error = "Vous n'avez pas l'autorisation de changer les mots de passe."
    } else {
      this.getDeliveryManDetails()
    }
  },

  methods: {
    async getDeliveryManDetails() {
      this.isLoading = true
      try {
        const { id } = this.$route.params
        const { data } = await axios.get(`/api/auth/users/delivery-men/${id}/`)
        this.deliveryMan = data
      } catch (error) {
        console.error('Error fetching delivery man details:', error)
        this.error = 'Erreur lors du chargement des informations du livreur'
      } finally {
        this.isLoading = false
      }
    },

    async onSubmit() {
      if (!this.canSubmit) return

      this.isSubmitting = true
      try {
        const { id } = this.$route.params
        await axios.post(`/api/auth/users/delivery-men/change-password/${id}/`, this.passwordForm)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.$router.push({ name: 'delivery-man-show', params: { id } })
      } catch (error) {
        console.error('Error changing password:', error)
        this.showToast('danger', error.response?.data?.error || 'Erreur lors du changement de mot de passe')
      } finally {
        this.isSubmitting = false
      }
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      }, {
        position: 'top-right',
      })
    },
  },
}
</script>
